import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, Star } from 'lucide-react';
import { classNames } from '@/lib/utils';
import type {
  BadgeListing,
  BandaPlan,
  CanalResena,
  EstadoPlan,
  RangoResenas,
  Reparto,
} from '@/lib/api';

/**
 * Piezas comunes a las tres pantallas de Reseñas (ranking, detalle y plan).
 *
 * Todo lo que hay aquí se pinta igual en las tres: si la nota de un piso es
 * verde en el ranking y ámbar en su ficha, el gestor deja de fiarse de las dos.
 * Por eso el color, el formato y los umbrales viven en un solo sitio.
 *
 * Los tipos son los de `@/lib/api`: el servidor manda el tema, la banda y la
 * etiqueta ya calculados, y duplicarlos aquí crearía dos verdades que divergen
 * en silencio.
 */

// ─── Formato ────────────────────────────────────────────────────────────────

/**
 * Número en español (coma decimal, punto de millares). `—` si no hay dato.
 *
 * Se usa en todas partes en lugar de `toFixed`, que escribe «4.21» y en una
 * pantalla en español se lee como cuatro mil doscientos veintiuno.
 */
export function fmt(n: number | null | undefined, decimales = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * `2026-08-19` → `19 ago 2026`.
 *
 * Se parte la cadena a mano en vez de pasar por `new Date`: las fechas de las
 * reseñas son días sueltos sin hora, y `new Date('2026-08-19')` las interpreta
 * como medianoche UTC, que a la izquierda del meridiano es el día anterior.
 * Acepta también un ISO con hora, del que solo se queda el día.
 */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const [, anio, mes, dia] = m;
  return `${Number(dia)} ${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}

/**
 * Antigüedad en palabras: «hoy», «ayer», «hace 12 días», «hace 3 meses».
 *
 * Devuelve cadena vacía si no hay fecha, para que el llamante pueda esconder la
 * línea entera con `&&` en vez de escribir «última —».
 */
export function hace(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const entonces = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const hoy = new Date();
  const ahora = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const dias = Math.round((ahora - entonces) / 86_400_000);

  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  if (dias < 365) {
    const meses = Math.max(1, Math.round(dias / 30.44));
    return meses === 1 ? 'hace 1 mes' : `hace ${meses} meses`;
  }
  const anios = Math.floor(dias / 365.25);
  return anios <= 1 ? 'hace 1 año' : `hace ${anios} años`;
}

// ─── Notas ──────────────────────────────────────────────────────────────────

/**
 * Color de la nota en escala de 5. Tres tramos, no un degradado: el gestor
 * decide «voy o no voy», y para eso hacen falta tres estados, no doce.
 * Sin nota → gris, que no es ni bueno ni malo.
 */
export function colorNota(nota: number | null): string {
  if (nota === null || !Number.isFinite(nota)) return 'text-slate-400';
  if (nota >= 4.6) return 'text-emerald-600';
  if (nota >= 4.3) return 'text-slate-700';
  return 'text-rose-600';
}

/** Escala tipográfica de `NotaGrande`. No confundir con la escala 5/10 del canal. */
export type TamanoNota = 'lg' | 'xl' | '2xl' | '3xl';

const CLASE_TAMANO: Record<TamanoNota, string> = {
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

/**
 * La nota del listing, siempre en escala de 5 y en `tabular-nums` para que las
 * cifras de una lista queden en columna aunque cada una tenga dígitos distintos.
 *
 * `escala` es el tamaño del texto, no la escala del canal (esa se enseña con
 * `EtiquetaCanal`). `atenuada` es para los pisos con badge «Pocas reseñas»: la
 * cifra existe pero no se sostiene, así que se pinta en gris en vez de en color.
 */
export function NotaGrande({
  nota,
  escala = 'xl',
  sinDatos = '—',
  atenuada = false,
  decimales = 2,
}: {
  nota: number | null;
  escala?: TamanoNota;
  sinDatos?: string;
  atenuada?: boolean;
  decimales?: number;
}) {
  const sin = nota === null || !Number.isFinite(nota);
  return (
    <span
      className={classNames(
        'font-bold tabular-nums leading-none whitespace-nowrap',
        CLASE_TAMANO[escala],
        atenuada && !sin ? 'text-slate-400' : colorNota(nota),
      )}
    >
      {sin ? sinDatos : fmt(nota, decimales)}
    </span>
  );
}

/**
 * Cinco estrellas con la última rellena a medias por ancho.
 *
 * Dos filas superpuestas —una gris entera y otra ámbar recortada— en lugar de
 * un icono de media estrella: media estrella solo sabe pintar 0,5, y aquí las
 * notas son 4,21 o 4,84. El recorte es exacto porque las cinco estrellas ocupan
 * celdas idénticas (mismo tamaño y mismo margen), así que el ancho en % coincide
 * con la nota sobre 5.
 */
export function Estrellas({ nota, size = 13 }: { nota: number | null; size?: number }) {
  if (nota === null || !Number.isFinite(nota)) {
    return <span className="text-slate-400">—</span>;
  }
  const pct = Math.max(0, Math.min(100, (nota / 5) * 100));
  const indices = [0, 1, 2, 3, 4];
  const etiqueta = `${fmt(nota, 2)} de 5`;

  return (
    <span
      className="relative inline-flex shrink-0 align-middle"
      role="img"
      aria-label={etiqueta}
      title={etiqueta}
    >
      <span className="inline-flex text-slate-300" aria-hidden="true">
        {indices.map((i) => (
          <Star key={i} size={size} className="shrink-0 mx-[0.5px]" />
        ))}
      </span>
      <span
        className="absolute inset-y-0 left-0 inline-flex overflow-hidden text-amber-500"
        style={{ width: `${pct}%` }}
        aria-hidden="true"
      >
        {indices.map((i) => (
          <Star key={i} size={size} className="shrink-0 mx-[0.5px] fill-amber-500" />
        ))}
      </span>
    </span>
  );
}

/**
 * Delta contra la ventana anterior: «▲ 0,3» o «▼ 0,4».
 *
 * No se pinta nada si es `null` (muestra insuficiente para que el dato
 * signifique algo) ni si es exactamente 0: «no ha cambiado» no es una noticia y
 * ocuparía sitio en una tarjeta de 88 px. El signo va también en el
 * `aria-label` porque un lector de pantalla lee «▲» como cualquier cosa.
 */
export function Delta({ valor }: { valor: number | null }) {
  if (valor === null || !Number.isFinite(valor) || valor === 0) return null;
  const sube = valor > 0;
  const texto = fmt(Math.abs(valor), 1);
  return (
    <span
      className={classNames(
        'text-[11px] tabular-nums whitespace-nowrap',
        sube ? 'text-emerald-600' : 'text-rose-600',
      )}
      aria-label={`${sube ? 'sube' : 'baja'} ${texto} respecto al periodo anterior`}
    >
      <span aria-hidden="true">{sube ? '▲' : '▼'} </span>
      {texto}
    </span>
  );
}

// ─── Barras ─────────────────────────────────────────────────────────────────

const COLOR_ESTRELLA = ['bg-emerald-500', 'bg-emerald-300', 'bg-amber-300', 'bg-orange-400', 'bg-rose-500'];

/**
 * Reparto de reseñas por estrella, de 5★ a 1★, en cinco barritas.
 *
 * Cinco `div` y no un gráfico: en el ranking hay 65 tarjetas a la vez y 65
 * instancias de recharts en un móvil son la diferencia entre una lista fluida y
 * una que da tirones. Los tramos vacíos no se pintan para que el `gap-px` no
 * deje rayas sueltas donde no hay nada.
 *
 * `reparto` llega del servidor con el índice 0 = 1★ … 4 = 5★; aquí se recorre al
 * revés porque lo que se lee primero es cuántas cincos hay.
 */
export function BarraReparto({ reparto }: { reparto: Reparto }) {
  const total = reparto.reduce((a, b) => a + b, 0);
  const detalle = [5, 4, 3, 2, 1]
    .map((estrella) => `${estrella}★ ${reparto[estrella - 1]}`)
    .join(' · ');

  if (total === 0) {
    return <span className="inline-block w-24 h-1.5 rounded-full bg-slate-200" aria-hidden="true" />;
  }

  return (
    <span
      className="inline-flex w-24 gap-px align-middle"
      role="img"
      aria-label={`Reparto de notas: ${detalle}`}
      title={detalle}
    >
      {[5, 4, 3, 2, 1].map((estrella, i) => {
        const n = reparto[estrella - 1];
        if (n === 0) return null;
        return (
          <span
            key={estrella}
            className={classNames('block h-1.5 rounded-full', COLOR_ESTRELLA[i])}
            style={{ width: `${(n / total) * 100}%` }}
          />
        );
      })}
    </span>
  );
}

/** Color del relleno de una sub-nota. Mismos cortes que la nota global. */
function colorSubnota(sobre5: number, fiable: boolean): string {
  if (!fiable) return 'bg-slate-300';
  if (sobre5 >= 4.5) return 'bg-emerald-500';
  if (sobre5 >= 4) return 'bg-amber-400';
  return 'bg-rose-500';
}

/**
 * Una fila de sub-nota: etiqueta, barra y valor.
 *
 * Barras y nunca un radar: un hexágono en 343 px es ilegible y no se puede
 * comparar entre dos pisos. Con menos de 3 opiniones no se pinta barra —un 5,0
 * de dos personas dibujaría la barra más larga de la ficha— y entre 3 y 4 la
 * barra va en gris con el recuento al lado, que es decir «esto todavía no es un
 * dato» sin esconderlo.
 */
export function BarraSubnota({
  etiqueta,
  sobre5,
  n,
}: {
  etiqueta: string;
  sobre5: number | null;
  n: number;
}) {
  // `null` también cuando hay cifra pero la respaldan menos de 3 opiniones: es
  // el mismo «todavía no hay dato» y así el resto del componente lo trata igual.
  const valor = sobre5 !== null && Number.isFinite(sobre5) && n >= 3 ? sobre5 : null;
  const fiable = n >= 5;

  return (
    <div className="flex items-center gap-2 h-7">
      <span className="w-28 shrink-0 text-[12px] text-slate-600 truncate" title={etiqueta}>
        {etiqueta}
      </span>
      {valor !== null ? (
        <span
          className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden"
          role="img"
          aria-label={`${etiqueta}: ${fmt(valor, 1)} sobre 5 con ${n} opiniones`}
        >
          <span
            className={classNames('block h-2 rounded-full', colorSubnota(valor, fiable))}
            style={{ width: `${Math.max(0, Math.min(100, (valor / 5) * 100))}%` }}
          />
        </span>
      ) : (
        <span className="flex-1" aria-hidden="true" />
      )}
      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-slate-700">
        {fmt(valor, 1)}
      </span>
      {n < 5 && <span className="shrink-0 text-[10px] text-slate-400">(n={n})</span>}
    </div>
  );
}

// ─── Controles ──────────────────────────────────────────────────────────────

/**
 * Botón de filtro.
 *
 * 44 px de alto y no 40: el área de toque tiene que salir del propio botón, no
 * del margen de la fila que lo contenga. Con 40 px cada pantalla acababa
 * compensándolo por su cuenta —o no compensándolo—, y en la ficha de
 * alojamiento hicieron falta `items-stretch` y una fila de `h-12` para llegar a
 * los 44. Nada aquí depende de `:hover`, que en un móvil no existe, y
 * `aria-pressed` porque esto es un interruptor, no un enlace.
 */
export function Chip({
  activo,
  children,
  onClick,
  count,
}: {
  activo: boolean;
  children: ReactNode;
  onClick: () => void;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={classNames(
        'inline-flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-md text-sm whitespace-nowrap transition-colors',
        activo
          // `an-active` es el override del repo para que un chip activo no quede
          // azul marino sobre gris marino en modo oscuro.
          ? 'bg-sereno-dark text-white an-active'
          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5',
      )}
    >
      {children}
      {count !== undefined && (
        <span className={classNames('tabular-nums', activo ? 'opacity-80' : 'text-slate-400')}>
          {count.toLocaleString('es-ES')}
        </span>
      )}
    </button>
  );
}

export const RANGOS: { valor: RangoResenas; etiqueta: string }[] = [
  { valor: '3m', etiqueta: '3 m' },
  { valor: '6m', etiqueta: '6 m' },
  { valor: '12m', etiqueta: '12 m' },
  { valor: '24m', etiqueta: '24 m' },
  { valor: 'todo', etiqueta: 'Todo' },
];

/**
 * Selector de ventana.
 *
 * No se reutiliza el de Aperturas (30/90/180/400 días): con reseñas de 2019 a
 * 2026, «30 días» no significa nada, y además ese control lo comparten Aperturas
 * y Llamadas. 44 px de alto por el mismo motivo que `Chip`.
 *
 * Las cinco opciones caben justas en 375 px —medido: 300 px de los 343
 * disponibles—. El `overflow-x-auto` está por si alguna etiqueta futura las
 * saca de ahí: prefiero que la fila scrollee a que envuelva, porque envolviendo
 * el selector pasa a dos alturas y empuja el contenido de las tres pantallas
 * que lo usan.
 */
export function SelectorRango({
  rango,
  onChange,
}: {
  rango: RangoResenas;
  onChange: (r: RangoResenas) => void;
}) {
  return (
    <div
      className="inline-flex max-w-full overflow-x-auto scrollbar-none rounded-lg border border-slate-200 dark:border-white/10"
      role="group"
      aria-label="Periodo de las reseñas"
    >
      {RANGOS.map((r, i) => (
        <button
          key={r.valor}
          type="button"
          onClick={() => onChange(r.valor)}
          aria-pressed={rango === r.valor}
          className={classNames(
            'px-3 min-h-11 text-sm shrink-0 transition-colors',
            // Sin `overflow-hidden` en el contenedor —lo pide el scroll— las
            // esquinas hay que ponerlas en los extremos a mano.
            i === 0 && 'rounded-l-lg',
            i === RANGOS.length - 1 && 'rounded-r-lg',
            rango === r.valor
              ? 'bg-sereno-dark text-white an-active'
              : 'bg-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5',
          )}
        >
          {r.etiqueta}
        </button>
      ))}
    </div>
  );
}

const CLAVE_RANGO = 'reviews.rango';
const RANGO_POR_DEFECTO: RangoResenas = '24m';

function esRango(v: string | null): v is RangoResenas {
  return v === '3m' || v === '6m' || v === '12m' || v === '24m' || v === 'todo';
}

/**
 * Ventana de fechas de la sección, recordada entre visitas.
 *
 * Se persiste porque las tres pantallas comparten ventana y volver a elegirla
 * al cambiar de pestaña —o al volver del detalle de un piso— convierte el
 * control en un peaje. Por defecto 24 meses: 12 dejan fuera a los pisos con
 * pocas reseñas al año y «todo» mezcla el piso de 2019 con el de ahora.
 *
 * Toda lectura y escritura va en `try/catch`: Safari en navegación privada
 * lanza al tocar `localStorage` y eso tumbaría la sección entera.
 */
export function useRangoResenas(): { rango: RangoResenas; setRango: (r: RangoResenas) => void } {
  const [rango, setEstado] = useState<RangoResenas>(() => {
    try {
      const guardado = localStorage.getItem(CLAVE_RANGO);
      if (esRango(guardado)) return guardado;
    } catch {
      /* sin persistencia */
    }
    return RANGO_POR_DEFECTO;
  });

  const setRango = useCallback((r: RangoResenas) => {
    setEstado(r);
    try {
      localStorage.setItem(CLAVE_RANGO, r);
    } catch {
      /* sin persistencia */
    }
  }, []);

  return { rango, setRango };
}

// ─── Etiquetas del dominio ──────────────────────────────────────────────────

export const ETIQUETA_CANAL: Record<CanalResena, string> = {
  airbnb: 'Airbnb',
  booking: 'Booking',
  vrbo: 'Vrbo',
  otras: 'Otras',
};

/** Escala nativa de cada canal. Booking puntúa sobre 10; el resto, sobre 5. */
export const ESCALA_CANAL: Record<CanalResena, 5 | 10> = {
  airbnb: 5,
  booking: 10,
  vrbo: 5,
  otras: 5,
};

/**
 * «Booking 8,4/10 (12)» · «Airbnb 4,8 (22)».
 *
 * La nota de Booking se enseña en su escala nativa y con la unidad escrita: es
 * la cifra que el propietario ve en su extranet, y es la única forma de
 * explicar por qué su 8,4 aparece como 4,2 al lado de un Airbnb de 4,8. Va como
 * texto visible y nunca dentro de un `title` como único sitio: en un móvil no
 * hay hover y el dato quedaría inaccesible.
 */
export function EtiquetaCanal({
  canal,
  n,
  nota5,
  nativa,
  escala,
  decimales = 1,
}: {
  canal: CanalResena;
  n?: number;
  nota5?: number | null;
  nativa?: number | null;
  escala?: 5 | 10;
  decimales?: number;
}) {
  const esc = escala ?? ESCALA_CANAL[canal];
  const valor = esc === 10 ? nativa ?? null : nota5 ?? null;
  const hayNota = valor !== null && Number.isFinite(valor);

  return (
    <span className="whitespace-nowrap">
      {ETIQUETA_CANAL[canal]}
      {hayNota && (
        <>
          {' '}
          <span className="tabular-nums">
            {fmt(valor, decimales)}
            {esc === 10 && '/10'}
          </span>
        </>
      )}
      {n !== undefined && <span className="tabular-nums"> ({n.toLocaleString('es-ES')})</span>}
    </span>
  );
}

const TEXTO_BADGE: Record<BadgeListing, string> = {
  pocas: 'Pocas reseñas',
  sin_nota: 'Sin nota',
  edificio: 'Reseñas del edificio',
};

const AYUDA_BADGE: Record<BadgeListing, string> = {
  pocas: 'Menos de 8 notas: la media todavía se mueve mucho con cada reseña nueva',
  sin_nota: 'Ninguna reseña con nota en este periodo',
  edificio: 'Estas reseñas llegaron al listing padre porque el piso no tiene id propio en el canal',
};

/**
 * Avisos sobre la calidad del dato del piso.
 *
 * Se enseñan en vez de esconder el piso del ranking: sacarlo de la lista lo
 * vuelve invisible, y estos tres casos no son errores, son cómo llegan los datos
 * de los canales.
 */
export function BadgesListing({ badges }: { badges: BadgeListing[] }) {
  if (!badges.length) return null;
  return (
    <>
      {badges.map((b) => (
        <span
          key={b}
          className="badge bg-slate-100 text-slate-600 text-[10px]"
          title={AYUDA_BADGE[b]}
        >
          {TEXTO_BADGE[b]}
        </span>
      ))}
    </>
  );
}

// ─── Plan de mejora ─────────────────────────────────────────────────────────

/** Clases de `badge` por banda de prioridad. Rojo, ámbar y gris: tres, no doce. */
export const COLOR_BANDA: Record<BandaPlan, string> = {
  critico: 'bg-rose-100 text-rose-800',
  revisar: 'bg-amber-100 text-amber-800',
  menor: 'bg-slate-100 text-slate-700',
};

export const ETIQUETA_BANDA: Record<BandaPlan, string> = {
  critico: 'Crítico',
  revisar: 'A revisar',
  menor: 'Menor',
};

export const ETIQUETA_ESTADO: Record<EstadoPlan, string> = {
  pendiente: 'Pendiente',
  en_curso: 'En curso',
  resuelto: 'Resuelto',
  descartado: 'Descartado',
};

// ─── Scroll infinito ────────────────────────────────────────────────────────

/** A cuánto del final se pide la página siguiente. */
const MARGEN_CARGA_PX = 600;

/**
 * Carga la página siguiente cuando el final de la lista se acerca a la pantalla.
 *
 * Dos mecanismos, los dos automáticos, y a propósito:
 *
 *  - Un `IntersectionObserver` sobre un centinela al final de la lista. Es el
 *    bueno: no obliga a medir el documento en cada gesto, que es lo que hace
 *    que una lista larga vaya a tirones en un móvil.
 *  - Un listener de `scroll` pasivo como red. Sin él, cualquier situación en la
 *    que el observador no entregue callbacks —un navegador viejo, una pestaña
 *    que no compone frames— deja al usuario sin forma de cargar nada, porque
 *    aquí ya no hay botón.
 *
 * El listener se estrangula por reloj y no con `requestAnimationFrame`, que
 * sería lo natural: rAF no se ejecuta en una pestaña que no está pintando, y
 * entonces la red de seguridad se cae justo en el escenario para el que está
 * puesta. Medir el documento cada 150 ms como mucho cuesta lo mismo y funciona
 * siempre.
 *
 * `onCargar` se guarda en una ref: es una función nueva en cada render —depende
 * del número de elementos ya cargados— y meterla en las dependencias volvería a
 * montar los dos mecanismos constantemente.
 */
export function useScrollInfinito(
  onCargar: () => void,
  { activo, cargando }: { activo: boolean; cargando: boolean }
) {
  const centinela = useRef<HTMLDivElement | null>(null);
  const ultimaCarga = useRef(onCargar);
  ultimaCarga.current = onCargar;

  useEffect(() => {
    // Sin `activo` no se engancha nada: ya no quedan páginas, o hay una en vuelo
    // y disparar otra pediría dos veces el mismo índice.
    if (!activo || cargando) return;

    let pedido = false;
    const pedirUnaVez = () => {
      if (pedido) return;
      pedido = true;
      ultimaCarga.current();
    };

    const nodo = centinela.current;
    const observador =
      nodo && typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entradas) => {
              if (entradas.some((e) => e.isIntersecting)) pedirUnaVez();
            },
            { rootMargin: `${MARGEN_CARGA_PX}px 0px` }
          )
        : null;
    observador?.observe(nodo as Element);

    let ultimaMedida = 0;
    const alScrollear = () => {
      if (pedido) return;
      const ahora = Date.now();
      if (ahora - ultimaMedida < 150) return;
      ultimaMedida = ahora;
      const doc = document.documentElement;
      const restante = doc.scrollHeight - (window.scrollY + window.innerHeight);
      if (restante < MARGEN_CARGA_PX) pedirUnaVez();
    };

    window.addEventListener('scroll', alScrollear, { passive: true });
    window.addEventListener('resize', alScrollear, { passive: true });
    // Una comprobación de entrada: si la primera página no llena la pantalla,
    // no habrá ningún scroll que dispare nada y la lista se quedaría corta.
    alScrollear();

    return () => {
      observador?.disconnect();
      window.removeEventListener('scroll', alScrollear);
      window.removeEventListener('resize', alScrollear);
    };
  }, [activo, cargando]);

  return centinela;
}

/**
 * El pie de una lista con scroll infinito.
 *
 * Enseña el centinela invisible, el aviso de que está cargando y —solo si algo
 * falló— un botón para reintentar. El botón no está en el camino normal a
 * propósito: la lista se carga sola al bajar, y un «cargar más» permanente
 * invita a pulsarlo y compite con el gesto.
 */
export function PieInfinito({
  centinela,
  cargando,
  error,
  onReintentar,
  agotadoTexto,
  mostrarAgotado,
}: {
  centinela: React.MutableRefObject<HTMLDivElement | null>;
  cargando: boolean;
  error: string | null;
  onReintentar: () => void;
  agotadoTexto?: string;
  mostrarAgotado?: boolean;
}) {
  return (
    <div className="pt-1">
      {/* Un píxel de alto y no cero: un elemento sin área no llega a
          intersectar en algunos navegadores y el observador no dispararía. */}
      <div ref={centinela} className="h-px" aria-hidden="true" />
      {cargando && (
        <p className="flex items-center justify-center gap-2 h-12 text-sm text-slate-500">
          <Loader2 className="animate-spin" size={16} />
          Cargando más
        </p>
      )}
      {error && (
        <div className="flex flex-col items-center gap-2 py-3">
          <p className="text-sm text-amber-700 dark:text-amber-300">{error}</p>
          <button type="button" onClick={onReintentar} className="btn-secondary min-h-11 py-0 text-sm">
            Reintentar
          </button>
        </div>
      )}
      {!cargando && !error && mostrarAgotado && agotadoTexto && (
        <p className="text-center text-[12px] text-slate-400 py-3">{agotadoTexto}</p>
      )}
    </div>
  );
}
